<template>
  <div class="space-y-2">
    <div class="flex gap-3 items-center justify-between">
      <p
        class="dark:text-content-inverse font-medium text-content-level-900 text-sm"
        data-gratuity-summary
      >
        <span data-gratuity-percent>{{ percentText }}</span>

        <span aria-hidden="true"> · </span>

        <span data-gratuity-amount>{{ amountText }}</span>
      </p>

      <Toggle
        :model-value="isAmountMode"
        :disabled="!canSwitchToPercent"
        :ariaLabel="$t('commerce.gratuitySelector.modeLabel')"
        size="sm"
        @update:model-value="switchMode"
      >
        <span class="dark:text-content-level-400 text-content-level-600 text-xs">{{
          isAmountMode
            ? $t('commerce.gratuitySelector.modeAmount')
            : $t('commerce.gratuitySelector.modePercent')
        }}</span>
      </Toggle>
    </div>

    <div class="flex justify-end">
      <InputCurrency
        v-if="isAmountMode"
        :model-value="amount"
        v-bind="amountInputAttributes"
        :invalid="amountError !== ''"
        wrapper-class="w-32"
        text-align="right"
        @update:model-value="handleAmountInput"
      />

      <UInput
        v-else
        v-bind="inputBindings"
        :model-value="shownText"
        :ui="inputUi"
        class="w-24"
        @update:model-value="handleInput"
        @blur="handleBlur"
      />
    </div>

    <p
      v-if="isAmountMode && amountError !== ''"
      role="alert"
      class="dark:text-danger-400 text-danger-600 text-right text-sm"
    >
      {{ amountError }}
    </p>

    <div class="px-2">
      <div class="h-8 relative">
        <button
          v-for="tick in ticks"
          :key="tick.key"
          type="button"
          :aria-label="tick.ariaLabel"
          :aria-pressed="tick.pressed"
          :disabled="tick.disabled"
          class="-translate-x-1/2 absolute cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 flex flex-col items-center text-xs"
          :class="
            tick.pressed
              ? 'font-semibold text-primary'
              : 'dark:text-content-level-400 hover:text-primary text-content-level-600'
          "
          :style="{ left: tickOffset(tick.fraction) }"
          @click="() => selectTick(tick.selection)"
        >
          <span>{{ tick.label }}</span>

          <span
            class="bg-border-level-300 dark:bg-border-level-600 h-2 mt-0.5 w-px"
            aria-hidden="true"
          />
        </button>
      </div>

      <SliderField
        :model-value="sliderValue"
        :min="slider.min"
        :max="slider.max"
        :step="slider.step"
        :snap-points="slider.snapPoints"
        :snap-radius="slider.snapRadius"
        :ariaLabel="sliderLabel"
        :ariaValuetext="isAmountMode ? amountText : percentText"
        name="gratuity_percentage"
        @update:model-value="handleSlider"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { UInput } from '#components'

import { InputCurrency } from '~/components/forms/inputs'
import { useGratuitySelector } from '~/composables/useGratuitySelector'
import Toggle from '~/design-system/atoms/Toggle/Toggle.atom.vue'
import { fieldControlInput } from '~/design-system/base/fieldControlInput'
import SliderField from '~/design-system/molecules/SliderField.molecule.vue'
import type { GratuitySelection } from '~/types/GratuitySelection'

const props = defineProps<{
  /** The gratuity choice: a percentage of the subtotal or a fixed amount. */
  modelValue: GratuitySelection
  /** The subtotal the percentage and the amount convert through. */
  subtotal: number | string
}>()

const emit = defineEmits<{
  'update:modelValue': [selection: GratuitySelection]
}>()

const {
  amount,
  amountError,
  amountInputAttributes,
  amountText,
  canSwitchToPercent,
  handleAmountInput,
  handleBlur,
  handleInput,
  handleSlider,
  inputAttributes,
  isAmountMode,
  percentText,
  selectTick,
  shownText,
  slider,
  sliderLabel,
  sliderValue,
  switchMode,
  tickOffset,
  ticks
} = useGratuitySelector({
  emitSelection: (selection) => {
    emit('update:modelValue', selection)
  },
  modelValue: () => props.modelValue,
  subtotal: () => props.subtotal
})

const inputUi = fieldControlInput.ui(false, 'text-right')
const inputBindings = computed(() => ({ ...fieldControlInput.props, ...inputAttributes.value }))
</script>
